import classnames from "classnames";
import get from "lodash-es/get";
import isEmpty from "lodash-es/isEmpty";
import React, { memo } from "react";

import { testAttr } from "@/browser";

import { Flex } from "../Flex";
import { GroupRowComponent as GroupRowComponentBase } from "./components/GroupRowComponent";
import { HeaderComponent as HeaderComponentBase } from "./components/HeaderComponent";
import { NoData as NoDataBase } from "./components/NoData";
import { useExpand } from "./hooks";
import { IDataTableProps, IExpandIcons } from "./types";

const DEFAULT_EXPAND_ICONS: IExpandIcons = {
  expandAllIcon: "double_arrow_bottom",
  collapseAllIcon: "double_arrow_top",
  expandIcon: "chevron_down",
  collapseIcon: "chevron_up",
};

export type TDataTable = <T>(props: IDataTableProps<T>) => React.ReactElement<IDataTableProps<T>> | null;

const DataTableComponent: TDataTable = props => {
  const {
    autoHeight,
    className,
    columns,
    data,
    ExpandedRowComponent,
    expandIcons = DEFAULT_EXPAND_ICONS,
    HeaderComponent,
    NoDataComponent,
    RowComponent,
    uniqueKeyAccessor,
    GroupRowComponent,
    preExpandRowsData,
    testName,
  } = props;
  const expandState = useExpand(data, uniqueKeyAccessor, preExpandRowsData);

  const HeaderRow = HeaderComponent ? HeaderComponent : HeaderComponentBase;
  const NoData = NoDataComponent ? NoDataComponent : NoDataBase;
  const GroupRow = GroupRowComponent ? GroupRowComponent : GroupRowComponentBase;

  const classNames = classnames("data-table", className);
  const tableTestableProps = testAttr(props, { sufix: "data-table" });

  const contentClassNames = classnames("data-table__content");
  const contentTestableProps = testAttr(props, { sufix: "data-table__content" });

  return (
    <Flex direction="column" className={classNames} {...tableTestableProps}>
      <HeaderRow
        allRowsExpanded={expandState.allExpanded}
        collapseAllIcon={expandIcons.collapseAllIcon}
        columns={columns}
        expandable={!!ExpandedRowComponent}
        expandAllIcon={expandIcons.expandAllIcon}
        onToggleExpandClick={expandState.toggleExpandAll}
        testName={testName}
      />
      <Flex direction="column" className={contentClassNames} {...contentTestableProps}>
        {!isEmpty(data) ? (
          data.map((rowData, index) => {
            const key = get(rowData, uniqueKeyAccessor) || index;

            return (
              <GroupRow
                autoHeight={autoHeight}
                collapseIcon={expandIcons.collapseIcon}
                columns={columns}
                ExpandedRowComponent={ExpandedRowComponent}
                expandIcon={expandIcons.expandIcon}
                index={index}
                isExpanded={expandState.isRowExpanded(rowData)}
                key={`gr-${key}`}
                onExpandRowClick={expandState.toggleExpandRow}
                RowComponent={RowComponent}
                rowData={rowData}
                testName={testName}
              />
            );
          })
        ) : (
          <NoData testName={testName} />
        )}
      </Flex>
    </Flex>
  );
};

export const DataTable = memo(DataTableComponent) as TDataTable;
